import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    DndContext,
    DragOverlay,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    pointerWithin,
    rectIntersection,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import type { CollisionDetection, DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { AppIcon } from '../ui/icon'
import { mdiChevronDown, mdiChevronRight, mdiDragVertical, mdiFolder, mdiPlus } from '@mdi/js'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { cn } from '../../lib/utils'
import {
    ROOT_KEY,
    buildHierarchy,
    groupChildrenByParent,
    numberHierarchy,
    parentKeyOf,
} from './outline-numbering'
import type { OutlineTreeNode } from './outline-numbering'
import { resolveTreeIcon } from './tree-icons'
import type { HierarchyTreeIcons } from './tree-icons'
import { EmptyState } from '../ui/empty-state'

export { computeOutlineNumbers } from './outline-numbering'
export { resolveTreeIcon } from './tree-icons'
export type { HierarchyTreeIcons } from './tree-icons'

/**
 * Generic node consumed by {@link HierarchyTree}. Consumers map their own
 * entities (folder definitions, document types, asset types, …) onto this shape.
 */
export interface HierarchyTreeNode {
    uid: string
    /** Already-localized label. The tree never translates anything itself. */
    label: string
    parentUid?: string | null
    /** Only meaningful when the tree is `orderable`; ignored otherwise. */
    ordering?: number
    /**
     * MDI path overriding the row icon for this node only. Highest priority —
     * it wins over the tree-level {@link HierarchyTreeProps.icons}.
     */
    icon?: string
    /** Small trailing badge (e.g. the document type a folder claims). */
    badge?: string
}

/**
 * Emitted when a drag & drop in edit mode changes the hierarchy.
 *
 * `siblingOrder` is the full ordered list of the destination parent's children
 * (including the moved node) *after* the move, so a consumer that persists an
 * explicit ordering can renumber only the siblings whose index actually
 * changed. Consumers that render alphabetically simply ignore it.
 */
export interface HierarchyMove {
    uid: string
    parentUid: string | null
    /** Index of the moved node inside `siblingOrder`. Undefined when `orderable` is false. */
    ordering?: number
    siblingOrder: string[]
}

/**
 * Every field is **required on purpose**: this component never calls `t()`
 * (see the note on {@link HierarchyTreeNode.label}), so an optional field with
 * an English default would silently ship untranslated copy to any consumer
 * that forgot it. Making them mandatory turns that into a compile error.
 */
export interface HierarchyTreeLabels {
    /** Button that turns edit mode on. */
    edit: string
    /** Button that turns edit mode off. */
    done: string
    /** Root drop target label, shown in edit mode. */
    root: string
    /** Hint next to the root drop target. */
    dropToRoot: string
    /** Shown when there is no node at all. */
    empty: string
    /** Tooltip of the per-row "add child" button. */
    addChild: string
}

export interface HierarchyTreeProps {
    nodes: HierarchyTreeNode[]
    selectedUid?: string | null
    onSelect?: (uid: string) => void
    /** Renders the edit-mode toggle in the tree header. */
    editable?: boolean
    /** Controlled edit-mode flag. Drag & drop only works while this is true. */
    editing?: boolean
    onEditingChange?: (editing: boolean) => void
    /**
     * When true the tree sorts by `ordering` and exposes sibling-reorder drop
     * zones. When false siblings render alphabetically and drag & drop only
     * re-parents.
     */
    orderable?: boolean
    onMove?: (move: HierarchyMove) => void
    /** Renders a per-row "add child" button, plus one on the root drop target. */
    onCreateChild?: (parentUid: string | null) => void
    /** Already-localized copy. Required — the tree never translates anything. */
    labels: HierarchyTreeLabels
    /** Prefix for every `data-testid` this tree emits. */
    testIdPrefix?: string
    defaultExpanded?: 'none' | 'all'
    /**
     * Renders a computed outline number (`1`, `1.1`, `1.1.1`, `2` …) before
     * each label. Derived from the rendered sibling order — never persisted.
     */
    numbered?: boolean
    className?: string
    /**
     * Row-icon defaults for this tree. Useful when every node is the same kind
     * of thing — a folder layout has no "file" leaves, so it swaps the leaf
     * glyph once here instead of per node. Omitted keys keep the historical
     * defaults (`mdiFileOutline` / `mdiFolder` / `mdiFolderOpen`).
     */
    icons?: HierarchyTreeIcons
    /** Extra controls rendered on the right of the tree header. */
    headerActions?: React.ReactNode
    title?: string
}

type InternalNode = OutlineTreeNode<HierarchyTreeNode>

const ROOT_DROP_ID = 'hierarchy-root'

const collisionDetection: CollisionDetection = (args) => {
    const pointerCollisions = pointerWithin(args)
    if (pointerCollisions.length > 0) return pointerCollisions
    return rectIntersection(args)
}

export function HierarchyTree({
    nodes,
    selectedUid,
    onSelect,
    editable = false,
    editing = false,
    onEditingChange,
    orderable = false,
    onMove,
    onCreateChild,
    labels,
    testIdPrefix = 'hierarchy',
    defaultExpanded = 'none',
    numbered = false,
    className,
    icons,
    headerActions,
    title,
}: HierarchyTreeProps) {
    const [expanded, setExpanded] = useState<Set<string>>(new Set())
    const [activeUid, setActiveUid] = useState<string | null>(null)
    const initialisedRef = useRef(false)

    const isEditing = editable && editing

    // A lone PointerSensor with a distance constraint is mouse-only: on a touch
    // screen the first pointermove past the threshold is also a scroll gesture,
    // the browser takes over and fires `pointercancel`, and the drag aborts.
    // Mouse keeps the distance threshold; touch uses a long-press delay so a
    // normal swipe below the tolerance still scrolls the panel.
    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
        useSensor(KeyboardSensor)
    )

    // Children indexed by parent key, sorted per the consumer's mode.
    const childrenByParent = useMemo(
        () => groupChildrenByParent(nodes, orderable),
        [nodes, orderable]
    )

    const tree = useMemo(
        () => buildHierarchy(nodes, childrenByParent),
        [childrenByParent, nodes]
    )

    // Outline numbering follows the rendered order, so it is derived from the
    // built tree and recomputed on every change (including after a drop).
    const outlineNumbers = useMemo(
        () => (numbered ? numberHierarchy(tree) : null),
        [numbered, tree]
    )

    // Expand everything once when the consumer asked for it.
    useEffect(() => {
        if (initialisedRef.current || nodes.length === 0) return
        initialisedRef.current = true
        if (defaultExpanded === 'all') {
            setExpanded(new Set(nodes.map(n => n.uid)))
        }
    }, [nodes, defaultExpanded])

    const parentOf = useMemo(() => {
        const map = new Map<string, string | null>()
        for (const node of nodes) {
            const key = parentKeyOf(node.parentUid)
            map.set(node.uid, key === ROOT_KEY ? null : key)
        }
        return map
    }, [nodes])

    // Keep the selected node reachable: expand all of its ancestors.
    useEffect(() => {
        if (!selectedUid) return
        setExpanded(prev => {
            const next = new Set(prev)
            let cursor = parentOf.get(selectedUid) ?? null
            let guard = 0
            let changed = false
            while (cursor && guard++ < 100) {
                if (!next.has(cursor)) {
                    next.add(cursor)
                    changed = true
                }
                cursor = parentOf.get(cursor) ?? null
            }
            return changed ? next : prev
        })
    }, [selectedUid, parentOf])

    const toggle = useCallback((uid: string) => {
        setExpanded(prev => {
            const next = new Set(prev)
            if (next.has(uid)) next.delete(uid)
            else next.add(uid)
            return next
        })
    }, [])

    const descendantsOf = useCallback((uid: string): Set<string> => {
        const result = new Set<string>()
        const walk = (parentUid: string) => {
            for (const child of childrenByParent.get(parentUid) || []) {
                if (result.has(child.uid)) continue
                result.add(child.uid)
                walk(child.uid)
            }
        }
        walk(uid)
        return result
    }, [childrenByParent])

    const buildMove = useCallback((uid: string, overId: string): HierarchyMove | null => {
        const forbidden = descendantsOf(uid)
        const currentParent = parentOf.get(uid) ?? null

        const orderedUids = (parentUid: string | null) =>
            (childrenByParent.get(parentKeyOf(parentUid)) || []).map(n => n.uid)

        if (overId.startsWith('gap:')) {
            if (!orderable) return null
            const rest = overId.slice(4)
            const sep = rest.lastIndexOf(':')
            const parentKey = rest.slice(0, sep)
            const index = Number(rest.slice(sep + 1))
            const parentUid = parentKey === ROOT_KEY ? null : parentKey
            if (parentUid && (parentUid === uid || forbidden.has(parentUid))) return null

            const siblings = orderedUids(parentUid)
            const currentIndex = siblings.indexOf(uid)
            const withoutMoved = siblings.filter(u => u !== uid)
            let target = index
            if (currentIndex !== -1 && currentIndex < index) target -= 1
            target = Math.max(0, Math.min(target, withoutMoved.length))
            const siblingOrder = [
                ...withoutMoved.slice(0, target),
                uid,
                ...withoutMoved.slice(target),
            ]
            if (parentUid === currentParent && siblingOrder.join() === siblings.join()) return null
            return { uid, parentUid, ordering: target, siblingOrder }
        }

        let parentUid: string | null
        if (overId === ROOT_DROP_ID) {
            parentUid = null
        } else if (overId.startsWith('node:')) {
            parentUid = overId.slice(5)
        } else {
            return null
        }
        if (parentUid !== null && (parentUid === uid || forbidden.has(parentUid))) return null

        const siblings = orderedUids(parentUid)
        const withoutMoved = siblings.filter(u => u !== uid)
        // Dropping a node onto its current parent when it is already last is a no-op.
        if (parentUid === currentParent && siblings[siblings.length - 1] === uid) return null
        const siblingOrder = [...withoutMoved, uid]
        return {
            uid,
            parentUid,
            ordering: orderable ? siblingOrder.length - 1 : undefined,
            siblingOrder,
        }
    }, [childrenByParent, descendantsOf, orderable, parentOf])

    const handleDragStart = (event: DragStartEvent) => {
        setActiveUid(String(event.active.id).replace(/^drag:/, ''))
    }

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event
        setActiveUid(null)
        if (!over) return
        const uid = String(active.id).replace(/^drag:/, '')
        const move = buildMove(uid, String(over.id))
        if (!move) return
        if (move.parentUid) {
            setExpanded(prev => (prev.has(move.parentUid as string) ? prev : new Set(prev).add(move.parentUid as string)))
        }
        onMove?.(move)
    }

    const activeNode = activeUid ? nodes.find(n => n.uid === activeUid) : null

    const renderSiblings = (siblings: InternalNode[], parentUid: string | null, level: number) => {
        const parentKey = parentKeyOf(parentUid)
        const rows: React.ReactNode[] = []
        siblings.forEach((node, index) => {
            if (isEditing && orderable) {
                rows.push(
                    <DropGap
                        key={`gap-${parentKey}-${index}`}
                        id={`gap:${parentKey}:${index}`}
                        level={level}
                        testId={`${testIdPrefix}-gap-${parentKey}-${index}`}
                    />
                )
            }
            rows.push(
                <TreeRow
                    key={node.uid}
                    node={node}
                    isEditing={isEditing}
                    isSelected={selectedUid === node.uid}
                    isExpanded={expanded.has(node.uid)}
                    onToggle={() => toggle(node.uid)}
                    onSelect={() => onSelect?.(node.uid)}
                    onCreateChild={onCreateChild ? () => onCreateChild(node.uid) : undefined}
                    addChildLabel={labels.addChild}
                    testIdPrefix={testIdPrefix}
                    number={outlineNumbers?.get(node.uid)}
                    icons={icons}
                />
            )
            if (node.children.length > 0 && expanded.has(node.uid)) {
                rows.push(
                    <div key={`children-${node.uid}`} role="group">
                        {renderSiblings(node.children, node.uid, level + 1)}
                    </div>
                )
            }
        })
        if (isEditing && orderable) {
            rows.push(
                <DropGap
                    key={`gap-${parentKey}-${siblings.length}`}
                    id={`gap:${parentKey}:${siblings.length}`}
                    level={level}
                    testId={`${testIdPrefix}-gap-${parentKey}-${siblings.length}`}
                />
            )
        }
        return rows
    }

    const body = (
        <div className="p-2" data-testid={`${testIdPrefix}-tree`}>
            {isEditing && <RootDropTarget labels={labels} testIdPrefix={testIdPrefix} onCreateChild={onCreateChild} />}
            {nodes.length === 0 ? (
                <EmptyState
                    variant="inline"
                    title={labels.empty}
                    data-testid={`${testIdPrefix}-empty`}
                />
            ) : (
                <div role="tree">{renderSiblings(tree, null, 0)}</div>
            )}
        </div>
    )

    return (
        <div className={cn('flex flex-col min-h-0', className)}>
            {(title || editable || headerActions) && (
                <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
                    <span className="text-sm font-medium truncate">{title}</span>
                    <div className="flex items-center gap-1">
                        {headerActions}
                        {editable && (
                            <Button
                                variant={isEditing ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => onEditingChange?.(!isEditing)}
                                aria-label={isEditing ? labels.done : labels.edit}
                                title={isEditing ? labels.done : labels.edit}
                                data-testid={`${testIdPrefix}-edit-mode-toggle`}
                                aria-pressed={isEditing}
                            >
                                <AppIcon path={mdiDragVertical} size="sm" className="md:mr-1" />
                                <span className="hidden md:inline">
                                    {isEditing ? labels.done : labels.edit}
                                </span>
                            </Button>
                        )}
                    </div>
                </div>
            )}
            <div className="flex-1 min-h-0 overflow-y-auto">
                {isEditing ? (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={collisionDetection}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDragCancel={() => setActiveUid(null)}
                    >
                        {body}
                        <DragOverlay>
                            {activeNode ? (
                                <div className="rounded-lg border border-primary bg-background px-2 py-1 shadow-lg">
                                    <span className="text-sm font-medium">{activeNode.label}</span>
                                </div>
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                ) : (
                    body
                )}
            </div>
        </div>
    )
}

function DropGap({ id, level, testId }: { id: string; level: number; testId: string }) {
    const { setNodeRef, isOver } = useDroppable({ id })
    return (
        <div
            ref={setNodeRef}
            data-testid={testId}
            aria-hidden="true"
            className="h-3 flex items-center"
            style={{ paddingLeft: `${level * 1 + 0.5}rem` }}
        >
            <div
                className={cn(
                    'h-0.5 w-full rounded-full transition-colors',
                    isOver ? 'bg-primary' : 'bg-transparent'
                )}
            />
        </div>
    )
}

function RootDropTarget({
    labels,
    testIdPrefix,
    onCreateChild,
}: {
    labels: HierarchyTreeLabels
    testIdPrefix: string
    onCreateChild?: (parentUid: string | null) => void
}) {
    const { setNodeRef, isOver } = useDroppable({ id: ROOT_DROP_ID })
    return (
        <div
            ref={setNodeRef}
            data-testid={`${testIdPrefix}-root-drop`}
            className={cn(
                'group mb-1 flex items-center gap-2 rounded-lg border border-dashed px-2 py-2 transition-colors',
                isOver ? 'border-primary bg-primary/10' : 'border-muted'
            )}
        >
            <AppIcon path={mdiFolder} size="sm" className="text-primary shrink-0" />
            <span className="text-sm font-medium truncate">{labels.root}</span>
            <Badge variant="outline" className="text-xs ml-auto shrink-0">
                {labels.dropToRoot}
            </Badge>
            {onCreateChild && (
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    onClick={() => onCreateChild(null)}
                    aria-label={labels.addChild}
                    title={labels.addChild}
                    data-testid={`${testIdPrefix}-add-root`}
                >
                    <AppIcon path={mdiPlus} size="sm" />
                </Button>
            )}
        </div>
    )
}

interface TreeRowProps {
    node: InternalNode
    isEditing: boolean
    isSelected: boolean
    isExpanded: boolean
    onToggle: () => void
    onSelect: () => void
    onCreateChild?: () => void
    addChildLabel: string
    testIdPrefix: string
    /** Computed outline number (`1.2.1`), when the tree is `numbered`. */
    number?: string
    /** Tree-level icon defaults; `node.icon` still wins over them. */
    icons?: HierarchyTreeIcons
}

function TreeRow({
    node,
    isEditing,
    isSelected,
    isExpanded,
    onToggle,
    onSelect,
    onCreateChild,
    addChildLabel,
    testIdPrefix,
    number,
    icons,
}: TreeRowProps) {
    const hasChildren = node.children.length > 0
    const draggable = useDraggable({ id: `drag:${node.uid}`, disabled: !isEditing })
    const droppable = useDroppable({ id: `node:${node.uid}`, disabled: !isEditing })

    const setRef = (el: HTMLElement | null) => {
        draggable.setNodeRef(el)
        droppable.setNodeRef(el)
    }

    const isDropTarget = isEditing && droppable.isOver && !draggable.isDragging

    return (
        <div
            ref={setRef}
            className={cn(
                'group flex items-center gap-1 rounded px-2 py-1 cursor-pointer transition-colors hover:bg-muted/50',
                isSelected && 'bg-primary/10 text-primary font-medium',
                draggable.isDragging && 'opacity-50',
                isDropTarget && 'ring-2 ring-primary bg-primary/5'
            )}
            style={{
                paddingLeft: `${node.level * 1 + 0.5}rem`,
                // In edit mode a long press must pick the row up, not open the
                // double-tap zoom or start a text selection.
                ...(isEditing ? { touchAction: 'manipulation' as const, userSelect: 'none' as const } : {}),
            }}
            // Spread dnd-kit's props *first*: its `attributes` carry
            // role="button" / tabIndex, and the row must stay a `treeitem` in
            // both modes — so the explicit ARIA below has to win.
            {...(isEditing ? draggable.listeners : {})}
            {...(isEditing ? draggable.attributes : {})}
            onClick={onSelect}
            // Keyboard navigation: Enter/Space selects, Right/Left expand and
            // collapse — the tree must be usable without a pointer.
            onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return
                // In edit mode Space hands over to dnd-kit's KeyboardSensor
                // (pick up, arrows move, Space drops) — the row's own handler
                // replaced dnd-kit's, so it has to delegate explicitly.
                if (isEditing && e.key === ' ') {
                    draggable.listeners?.onKeyDown?.(e)
                    return
                }
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onSelect()
                } else if (e.key === 'ArrowRight' && hasChildren && !isExpanded) {
                    e.preventDefault()
                    onToggle()
                } else if (e.key === 'ArrowLeft' && hasChildren && isExpanded) {
                    e.preventDefault()
                    onToggle()
                }
            }}
            role="treeitem"
            tabIndex={0}
            aria-selected={isSelected}
            aria-expanded={hasChildren ? isExpanded : undefined}
            data-testid={`${testIdPrefix}-node-${node.uid}`}
        >
            {isEditing && (
                <AppIcon tone="muted" path={mdiDragVertical} size="xs" className="shrink-0" />
            )}
            {hasChildren ? (
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 shrink-0"
                    onClick={(e) => {
                        e.stopPropagation()
                        onToggle()
                    }}
                    data-testid={`${testIdPrefix}-toggle-${node.uid}`}
                >
                    <AppIcon path={isExpanded ? mdiChevronDown : mdiChevronRight} size="sm" />
                </Button>
            ) : (
                <span className="w-6 shrink-0" />
            )}

            {number && (
                <span
                    className="text-xs text-muted-foreground font-mono shrink-0 tabular-nums"
                    data-testid={`${testIdPrefix}-number-${node.uid}`}
                >
                    {number}
                </span>
            )}

            {/* Wrapped in a span: the MDI icon component does not forward
                unknown props (data-testid) to the rendered <svg>, and tests
                need to be able to assert which glyph a row got. */}
            <span className="shrink-0" data-testid={`${testIdPrefix}-icon-${node.uid}`}>
                <AppIcon
                    path={resolveTreeIcon(node.icon, hasChildren, isExpanded, icons)}
                    size="sm"
                    className={cn('shrink-0', isSelected ? 'text-primary' : 'text-muted-foreground')}
                />
            </span>

            <span className="text-sm truncate flex-1">{node.label}</span>

            {node.badge && (
                <Badge variant="secondary" className="text-xs shrink-0 max-w-[9rem] truncate">
                    {node.badge}
                </Badge>
            )}

            {onCreateChild && !isEditing && (
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 focus:opacity-100"
                    onClick={(e) => {
                        e.stopPropagation()
                        onCreateChild()
                    }}
                    aria-label={addChildLabel}
                    title={addChildLabel}
                    data-testid={`${testIdPrefix}-add-child-${node.uid}`}
                >
                    <AppIcon path={mdiPlus} size="xs" />
                </Button>
            )}
        </div>
    )
}
